
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import IoTGatewaySummaryStats from './device-health/IoTGatewaySummaryStats';
import IoTGatewayTable from './device-health/IoTGatewayTable';

interface IoTGateway {
  deviceId: string;
  location: string;
  status: string;
  connectedDevices: number;
  uptimeHours: number;
  cpuUsage: number;
  memoryUsage: number;
  temperatureCelsius: number;
  firmwareVersion: string;
  networkStatus: {
    connectivityType: string;
    signalStrength: number;
    dataTransferred: number;
    lastPingTime: number;
  };
  maintenanceStatus: {
    lastMaintenance: string;
    nextScheduled: string;
    alerts: string[];
  };
}

interface IoTGatewayHealthSummaryProps {
  gateways: IoTGateway[];
  isLoading: boolean;
  onSelectDevice: (deviceId: string) => void;
  selectedFilter: string | null;
  onFilterSelect: (filter: string | null) => void;
}

const IoTGatewayHealthSummary: React.FC<IoTGatewayHealthSummaryProps> = ({ 
  gateways,
  isLoading,
  onSelectDevice,
  selectedFilter,
  onFilterSelect
}) => {
  // Calculate summary stats
  const totalGateways = gateways.length;
  const healthyGateways = gateways.filter(gateway => gateway.status === 'Healthy').length;
  const warningGateways = gateways.filter(gateway => gateway.status === 'Warning').length;
  const criticalGateways = gateways.filter(gateway => gateway.status === 'Critical').length;

  // Filter gateways based on selected filter
  const filteredGateways = selectedFilter === null || selectedFilter === 'all'
    ? gateways
    : gateways.filter(gateway => gateway.status === selectedFilter);

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>IoT Gateway Health Summary</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex justify-center items-center h-40">
            <p className="text-muted-foreground">Loading data...</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          IoT Gateway Health Summary
          <Badge className="ml-2">{totalGateways} Devices</Badge>
          {selectedFilter && selectedFilter !== 'all' && (
            <Badge variant="outline" className="ml-auto">
              Filtered by: {selectedFilter}
            </Badge>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <IoTGatewaySummaryStats 
          totalGateways={totalGateways}
          healthyGateways={healthyGateways}
          warningGateways={warningGateways}
          criticalGateways={criticalGateways}
          selectedFilter={selectedFilter}
          onFilterSelect={onFilterSelect}
        />

        <IoTGatewayTable 
          gateways={filteredGateways}
          onSelectDevice={onSelectDevice}
        />
      </CardContent>
    </Card>
  );
};

export default IoTGatewayHealthSummary;
